import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'
import LinkRoundedIcon from '@mui/icons-material/LinkRounded'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import {
  getSupportVideoSessionTokens,
  sendSupportBookedEventJoinLink,
} from 'src/supportPortal/services/supportBookedEventActionsService'

dayjs.extend(utc)

function LinkRow({ label, url, subLabel }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#334155' }}>{label}</Typography>
        <Typography sx={{ fontSize: 12, color: '#2563eb', wordBreak: 'break-all' }}>{url}</Typography>
        {subLabel ? (
          <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>{subLabel}</Typography>
        ) : null}
      </Box>
      <Tooltip title="Copy link">
        <IconButton
          size="small"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(url)
              toast.success('Link copied')
            } catch (error) {
              toast.error('Could not copy the link')
            }
          }}
        >
          <ContentCopyRoundedIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Open in new tab">
        <IconButton size="small" onClick={() => window.open(url, '_blank', 'noopener')}>
          <OpenInNewRoundedIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </Stack>
  )
}

/** Per-card "Joining Links" dialog: view/copy/open every session link + resend over WhatsApp. */
export default function SupportJoiningLinksDialog({ open, onClose, bookedEvent }) {
  const [tokens, setTokens] = useState([])
  const [loading, setLoading] = useState(false)
  const [resendingKind, setResendingKind] = useState('')

  useEffect(() => {
    if (!open || !bookedEvent?.id) {
      return undefined
    }
    let cancelled = false
    async function loadTokens() {
      setLoading(true)
      const tokenList = await getSupportVideoSessionTokens([bookedEvent.id])
      if (!cancelled) {
        setTokens(tokenList || [])
        setLoading(false)
      }
    }
    void loadTokens()
    return () => {
      cancelled = true
    }
  }, [bookedEvent?.id, open])

  async function handleResend(kind) {
    setResendingKind(kind)
    const result = await sendSupportBookedEventJoinLink({ bookedEventId: bookedEvent?.id, kind })
    setResendingKind('')
    if (result.ok) {
      toast.success(`${kind === 'secure' ? 'Secure' : 'Public'} join link sent to the patient`)
    } else {
      toast.error(result.message)
    }
  }

  const practitionerJoinUrl = bookedEvent?.slug ? `https://aumhum.com/l/${bookedEvent.slug}` : null

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <LinkRoundedIcon sx={{ color: '#4f46e5' }} />
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 17, color: '#0f172a' }}>
              Joining Links
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: '#64748b' }}>
              {bookedEvent?.patientInfoData?.name || bookedEvent?.userName || 'Patient'} ·{' '}
              {bookedEvent?.startTime
                ? dayjs.utc(bookedEvent.startTime).local().format('DD MMM, h:mm A')
                : 'time not set'}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ mt: 0.5 }}>
          {practitionerJoinUrl ? (
            <LinkRow
              label="Practitioner / session link"
              url={practitionerJoinUrl}
              subLabel="Signs the practitioner into the session room"
            />
          ) : null}
          {tokens.map((token) => (
            <LinkRow
              key={token.id || token.sessionSlug}
              label={`Patient session link${token.enabled === false ? ' (disabled)' : ''}`}
              url={`https://aumhum.com/v/${token.sessionSlug}`}
              subLabel={
                token.expiresAt
                  ? `Expires ${dayjs.utc(token.expiresAt).local().format('DD MMM, h:mm A')}`
                  : undefined
              }
            />
          ))}
          {loading ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={14} />
              <Typography sx={{ fontSize: 12, color: '#64748b' }}>Loading links…</Typography>
            </Stack>
          ) : null}
          {!loading && tokens.length === 0 && !practitionerJoinUrl ? (
            <Typography sx={{ fontSize: 12.5, color: '#94a3b8' }}>
              No joining links found for this booking.
            </Typography>
          ) : null}
          <Divider />
          <Typography sx={{ fontSize: 11.5, color: '#94a3b8' }}>
            Resends go to the patient&apos;s WhatsApp and are recorded in the support activity trail.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button
          size="small"
          variant="outlined"
          disabled={Boolean(resendingKind)}
          onClick={() => handleResend('secure')}
        >
          {resendingKind === 'secure' ? 'Sending…' : 'Resend Secure Link'}
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="warning"
          disabled={Boolean(resendingKind)}
          onClick={() => handleResend('public')}
        >
          {resendingKind === 'public' ? 'Sending…' : 'Resend Public Link'}
        </Button>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}
